import React, { useEffect, useState } from "react";
import { Grid, Box, Container } from "@mui/material";
import { ChooseSeatsHeader } from "./ChooseSeatsHeader";
import { RepsProductModal } from "../modals/RepsProductModal";
import { useChooseSeats } from "./useChooseSeats";
import { useRepsProduct } from "../useRepsProduct";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { validateInventorySelection } from "./utils.chooseSeats";
import { useDebounce } from "@/shared/base/hooks/useDebounce";
import { useAppStore } from "@/global/store";
import {
  selectAccessibility,
  selectPriceRangeSelected,
  selectSeatCount,
  selectSectionSelected,
} from "@/global/store/data/gameFilters";
import { useSelectActiveEvent } from "./useSelectActiveEvent";
import { selectProducts } from "@/global/store/data/purchaseSeats/cartManager";
import { selectInventory } from "@/global/store/data/purchaseSeats/inventories";
import { selectIsAuthenticating } from "@/global/store/data/isAuthenticating";
import {
  AdaInfoMessage,
  CartError,
  useChooseSeatsAnalytics,
} from "./choose-seats-helper";
import { withChooseSeatsPrefetch } from "./withChooseSeatsPrefetch";
import { VirtualizedInventoryGridV2 } from "./VirtualizedInventoryGridV2";
import { InteractiveSeatMap } from "../../InteractiveSeatMap/InteractiveSeatMap";
import { useInventoryPreviewModal } from "../InventoryPreview/InventoryPreviewModal";

export const ChooseSeats = withChooseSeatsPrefetch(function () {
  useChooseSeatsAnalytics();
  const teamConfig = useTeamConfig();
  const { inventoryType, uiConfigs } = teamConfig;
  const { activeEvent, cartEventActive } = useSelectActiveEvent();
  const getInventoriesDebouncer = useDebounce();
  const inventoryPreviewModal = useInventoryPreviewModal();
  const seatCount = useAppStore(selectSeatCount);
  const priceRange = useAppStore(selectPriceRangeSelected);
  const { isAda } = useAppStore(selectAccessibility);
  const selectedSection = useAppStore(selectSectionSelected);
  const cartProducts = useAppStore(selectProducts(activeEvent?.eventCode));
  const inventories = useAppStore(selectInventory(activeEvent?.eventCode));
  const selectedInventories = useAppStore(selectProducts(activeEvent?.eventCode)); // prettier-ignore
  const isAuthenticating = useAppStore(selectIsAuthenticating);
  const { isInventoriesValidated } = validateInventorySelection(cartProducts, {
    seatCount,
    inventoryType: isAda ? "ADA" : inventoryType,
  });
  const { states: repsStates, handlers: repsHandlers } = useRepsProduct();
  const {
    handleLoadInventories,
    handleInventoryMouseLeave,
    handleInventoryMouseOver,
    handleInventorySelect,
    handleGetInventoryFilters,
  } = useChooseSeats();
  const [isLoading, setIsLoading] = useState(true);

  const suiteLevelInventoryCardComponents =
    uiConfigs?.inventoryCardSettings?.suiteLevelInventoryCard?.renderBody;

  useEffect(() => {
    if (inventoryPreviewModal.isOpen) return;
    getInventoriesDebouncer(async () => {
      setIsLoading(true);
      await handleLoadInventories();
      setIsLoading(false);
    }, 800);
  }, [seatCount, priceRange[0], priceRange[1], isAda, selectedSection]);

  // The section list drives the seat map highlighting. It is fetched once per
  // event by withChooseSeatsPrefetch, but the inventory above reloads whenever
  // seatCount / isAda change - which left the map showing a stale set of
  // sections. Re-sync the sections (only) on the same inputs the API accepts.
  const isSectionsSyncMounted = React.useRef(false);
  useEffect(() => {
    if (!isSectionsSyncMounted.current) {
      isSectionsSyncMounted.current = true;
      return;
    }
    if (!activeEvent) return;
    handleGetInventoryFilters(activeEvent, undefined, { sectionsOnly: true });
  }, [seatCount, isAda]);

  return (
    <>
      <RepsProductModal
        activeStep={repsStates.activeStep}
        recipientUser={repsStates.recipientUser}
        handleBackStep={repsHandlers.handleBackStep}
        handleFailedStep={repsHandlers.handleFailedStep}
        handleConfirmAccount={repsHandlers.handleConfirmAccount}
        getCustomerAccountError={repsStates.getCustomerAccountError}
        handleValidateAccountId={repsHandlers.handleValidateAccountId}
        handleResetErrorMessage={repsHandlers.handleResetErrorMessage}
      />

      <Box pb={4}>
        <Box>
          <Container maxWidth="xl">
            <ChooseSeatsHeader isLoading={isLoading} />
          </Container>
        </Box>

        <Box mt={3}>
          <Container maxWidth="xl">
            <Grid container spacing={4}>
              <Grid
                item
                xs={12}
                md={6}
                height={"fit-content"}
                position={"relative"}
              >
                <InteractiveSeatMap />
              </Grid>
              <Grid item xs={12} md={6}>
                <AdaInfoMessage isVisible={isAda} />

                <CartError
                  errorMessage={cartEventActive?.errorMessage}
                  isVisible={
                    !!cartEventActive && !!cartEventActive?.errorMessage
                  }
                />

                <VirtualizedInventoryGridV2
                  inventories={inventories}
                  selectedInventories={selectedInventories}
                  isLoading={isLoading || isAuthenticating}
                  onSelect={handleInventorySelect}
                  onMouseEnterInventory={handleInventoryMouseOver}
                  onMouseLeaveInventory={handleInventoryMouseLeave}
                  isInventoriesValidated={isInventoriesValidated}
                />
              </Grid>

              {suiteLevelInventoryCardComponents &&
                suiteLevelInventoryCardComponents?.map((item) => (
                  <Box key={item.id}>{item.component()}</Box>
                ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
});
